'use client';

import { memo } from 'react';

import { Spinner } from '@air/primitive-spinner';

import { useImportProgressStore } from '~/stores/useImportProgressStore';
import { getImportProgressLabel } from '~/utils/getImportProgressLabel';

const formatBytes = (bytes: number): string => {
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

export const ImportProgressStrip = memo(() => {
  const imports = useImportProgressStore((state) => state.imports);
  const batch = useImportProgressStore((state) => state.batch);
  const importLaneOwner = useImportProgressStore((state) => state.importLaneOwner);
  const hasPendingImports = useImportProgressStore((state) => state.pendingImportQueue.length > 0);

  const fileNames = Object.keys(imports);
  const showStrip = batch != null || importLaneOwner != null || fileNames.length > 0 || hasPendingImports;

  if (!showStrip) {
    return null;
  }

  const totalBytes = Object.values(imports).reduce((sum, { bytes }) => sum + bytes, 0);
  const label = getImportProgressLabel({ batch, fileNames, hasPendingImports });

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-export-progress flex items-center gap-2 border-neutral-4 border-t bg-neutral-1 px-3 py-2"
      data-testid="import-progress-strip"
    >
      <Spinner className="size-3 shrink-0 text-neutral-9" />
      <output aria-live="polite" className="flex-1 truncate text-11 text-gray-10">
        {label}
      </output>
      {totalBytes > 0 && <span className="shrink-0 font-mono text-11 text-gray-10">{formatBytes(totalBytes)}</span>}
    </div>
  );
});

ImportProgressStrip.displayName = 'ImportProgressStrip';
